import React, { ChangeEvent, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
  FormControl,
  FormHelperText,
  Alert,
} from '@mui/material';
import { Controller, SubmitHandler, UseFormReturn, ControllerRenderProps } from 'react-hook-form';
import { useGetLanguagesQuery } from 'features/getLanguages/getLanguagesApi';
import { PrizeFormType, PrizeTypeEnum } from 'entities/prize/model';
import { fromPrizeTypeToHumanReadableText } from 'widgets/prize/lib';
import { toFirstLetterUppercase } from 'shared/utils';
import dayjs, { Dayjs } from 'dayjs';
import { UploadImage, UploadFile } from 'shared/ui/UploadComponents';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';

type PrizeFormPropsType = {
  title?: string;
  form: UseFormReturn<Partial<PrizeFormType>>;
  onSubmit: (data: Partial<PrizeFormType>) => SubmitHandler<Partial<PrizeFormType>>;
  textButton?: string;
};

type TabPanelTypeProps = {
  children?: React.ReactNode;
  index: number;
  value: number;
};

export const PrizeForm = ({ form, onSubmit, textButton = 'Создать' }: PrizeFormPropsType) => {
  const { data: languagesList, isSuccess: languagesListSuccess } = useGetLanguagesQuery(null);

  const { control, setValue, watch, handleSubmit, formState: { errors = {} } = {} } = form;

  const isGeneratedWatcher = watch('is_generated');

  const TabPanel = (props: TabPanelTypeProps) => {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Box sx={{ marginTop: '16px' }}>
            <Typography>{children}</Typography>
          </Box>
        )}
      </div>
    );
  };

  const [tabValue, setTabValue] = useState(0);
  const handleChangeTabValue = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  const handleInputChange = ({ target: { name, value } }: ChangeEvent<HTMLInputElement>) => {
    const fieldName = name as keyof Partial<PrizeFormType>;
    setValue?.(fieldName, value);
  };

  const handleDateChange = (value: Dayjs | null, onChange: (...event: any[]) => void) => {
    onChange(value ? dayjs(value).format('YYYY-MM-DDT00:00:00') : null);
  };

  const handleUploadCodeTableChange = (e: any, field: ControllerRenderProps<Partial<PrizeFormType>, 'prize_file'>) => {
    field.onChange(e);
  };

  const isButtonDisabled = Object.keys(errors).length > 0;

  return (
    <div>
      {languagesListSuccess && (
        <div>
          <Box sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={tabValue}
                onChange={handleChangeTabValue}
              >
                {languagesList.map((language, index) => (
                  <Tab
                    key={language.codename}
                    label={language.codename}
                    {...a11yProps(index)}
                  />
                ))}
              </Tabs>
            </Box>
            {languagesList.map((language, index) => (
              <TabPanel
                key={language.codename}
                value={tabValue}
                index={index}
              >
                <Controller
                  control={control}
                  name={`name_${language.codename}`}
                  shouldUnregister={false}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label={`Название приза ${language.codename}`}
                      size="small"
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      sx={{
                        marginBottom: '24px',
                      }}
                      value={field.value || ''}
                      onChange={handleInputChange}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name={`description_${language.codename}`}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      onChange={handleInputChange}
                      label={`Описание приза ${language.codename}`}
                      size="small"
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      sx={{
                        marginBottom: '24px',
                      }}
                      multiline
                      rows={4}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </TabPanel>
            ))}
          </Box>
        </div>
      )}

      <Controller
        name="category"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            defaultValue=""
            value={field.value ?? ''}
            select
            label="Тип приза"
            placeholder=""
            fullWidth
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              marginBottom: '24px',
            }}
            required
          >
            <MenuItem value={PrizeTypeEnum.PROMO}>
              {toFirstLetterUppercase(fromPrizeTypeToHumanReadableText(PrizeTypeEnum.PROMO))}
            </MenuItem>
            <MenuItem value={PrizeTypeEnum.COUPON}>
              {toFirstLetterUppercase(fromPrizeTypeToHumanReadableText(PrizeTypeEnum.COUPON))}
            </MenuItem>
            <MenuItem value={PrizeTypeEnum.CERTIFICATE}>
              {toFirstLetterUppercase(fromPrizeTypeToHumanReadableText(PrizeTypeEnum.CERTIFICATE))}
            </MenuItem>
          </TextField>
        )}
      />

      <Controller
        control={control}
        name="image"
        render={({ field: { value, onChange } }) => (
          <UploadImage
            value={value}
            onChange={onChange}
            textLabel="Основное фото"
            helperText="Рекомендуемый формат 1024px x 512px"
            textButton="Загрузить картинку"
            errors={errors.image}
          />
        )}
      />

      <Controller
        control={control}
        name="icon"
        render={({ field: { value, onChange } }) => (
          <UploadImage
            value={value}
            onChange={onChange}
            textLabel="Иконка списка"
            helperText="Рекомендуемый формат 256px x 128px"
            textButton="Загрузить иконку"
            errors={errors.icon}
          />
        )}
      />

      <Controller
        control={control}
        name="is_generated"
        render={({ field: { value, ...restField } }) => (
          <>
            <FormControlLabel
              sx={{
                marginTop: '24px',
              }}
              control={
                <Checkbox
                  checked={value}
                  color="primary"
                  {...restField}
                />
              }
              label="Сгенерировать таблицу кодов самостоятельно"
            />
            {isGeneratedWatcher && (
              <Alert severity="warning">
                Коды будут созданы случайным образом и не будет иметь отношения к какой либо из внешних систем
              </Alert>
            )}
          </>
        )}
      />

      <Controller
        control={control}
        name="prize_file"
        render={({ field }) => (
          <UploadFile
            onChange={(e) => handleUploadCodeTableChange(e, field)}
            isClear={isGeneratedWatcher}
            disabled={isGeneratedWatcher}
            textLabel="Таблица кодов"
            helperText="xls, xlsx"
            textButton="Загрузить таблицу кодов"
            errors={errors.prize_file}
          />
        )}
      />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box
          sx={{
            display: 'flex',
            marginTop: '30px',
          }}
        >
          <Controller
            control={control}
            name="min_start_date"
            defaultValue=""
            render={({ field: { value, onChange, ...restField } }) => (
              <FormControl error={!!errors.min_start_date}>
                <DatePicker
                  label="Начало действия"
                  value={value ? dayjs(value) : null}
                  onChange={(value) => handleDateChange(value, onChange)}
                  format="DD/MM/YYYY"
                  {...restField}
                />
                <FormHelperText>{errors.min_start_date && errors.min_start_date.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            marginTop: '30px',
          }}
        >
          <Controller
            control={control}
            name="max_end_date"
            defaultValue=""
            render={({ field: { value, onChange, ...restField } }) => (
              <FormControl error={!!errors.max_end_date}>
                <DatePicker
                  label="Окончание действия"
                  value={value ? dayjs(value) : null}
                  onChange={(value) => handleDateChange(value, onChange)}
                  format="DD/MM/YYYY"
                  {...restField}
                />
                <FormHelperText>{errors.max_end_date && errors.max_end_date.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Box>
      </LocalizationProvider>

      <Box
        sx={{
          display: 'flex',
          marginTop: '30px',
        }}
      >
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          sx={{ textTransform: 'none' }}
          disabled={isButtonDisabled}
        >
          {textButton}
        </Button>
      </Box>
    </div>
  );
};
